// frontend/src/pages/Resources.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    InventoryIcon as Inventory,
    AddIcon as Add,
    EditIcon as Edit,
    DeleteIcon as Delete,
    CloseIcon as X,
    CalendarIcon as Calendar,
    LocationIcon as Location,
    CheckIcon as Check,
    SearchIcon as Search
} from '../components/Icons';

const Resources = () => {
    const { API_URL, user } = useContext(AuthContext);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showModal, setShowModal] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [selectedResource, setSelectedResource] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        type: 'material',
        description: '',
        quantity: 1,
        location: '',
        condition: 'good',
        requiresApproval: false
    });

    const [bookingData, setBookingData] = useState({
        startDate: '',
        endDate: '',
        note: ''
    });

    const typeOptions = [
        { value: 'material', label: 'Material' },
        { value: 'vehicle', label: 'Fahrzeug' },
        { value: 'room', label: 'Raum' },
        { value: 'equipment', label: 'Ausrüstung' },
        { value: 'other', label: 'Sonstiges' }
    ];

    const conditionOptions = [
        { value: 'excellent', label: 'Ausgezeichnet', color: 'text-primary' },
        { value: 'good', label: 'Gut', color: 'text-secondary' },
        { value: 'fair', label: 'Akzeptabel', color: 'text-tertiary' },
        { value: 'poor', label: 'Schlecht', color: 'text-error' },
        { value: 'needs_repair', label: 'Reparatur nötig', color: 'text-error' }
    ];

    useEffect(() => {
        fetchResources();
        // eslint-disable-next-line
    }, [filterType]);

    const fetchResources = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const url = filterType === 'all'
                ? `${API_URL}/resources`
                : `${API_URL}/resources?type=${filterType}`;
            const { data } = await axios.get(url, config);
            setResources(data);
            setLoading(false);
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Laden' });
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };

            if (selectedResource) {
                await axios.put(`${API_URL}/resources/${selectedResource._id}`, formData, config);
                setMessage({ type: 'success', text: 'Ressource aktualisiert!' });
            } else {
                await axios.post(`${API_URL}/resources`, formData, config);
                setMessage({ type: 'success', text: 'Ressource erstellt!' });
            }

            setShowModal(false);
            resetForm();
            fetchResources();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Speichern' });
        }
    };

    const handleBooking = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.post(`${API_URL}/resources/${selectedResource._id}/book`, bookingData, config);
            setMessage({ type: 'success', text: 'Buchung erfolgreich!' });
            setShowBookingModal(false);
            setBookingData({ startDate: '', endDate: '', note: '' });
            fetchResources();
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Buchung fehlgeschlagen' });
        }
    };

    const deleteResource = async (id) => {
        if (!window.confirm('Ressource wirklich löschen?')) return;
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.delete(`${API_URL}/resources/${id}`, config);
            setMessage({ type: 'success', text: 'Ressource gelöscht!' });
            fetchResources();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
    };

    const openEditModal = (resource) => {
        setSelectedResource(resource);
        setFormData({
            name: resource.name,
            type: resource.type,
            description: resource.description || '',
            quantity: resource.quantity || 1,
            location: resource.location || '',
            condition: resource.condition || 'good',
            requiresApproval: resource.requiresApproval || false
        });
        setShowModal(true);
    };

    const openBookingModal = (resource) => {
        setSelectedResource(resource);
        setShowBookingModal(true);
    };

    const resetForm = () => {
        setSelectedResource(null);
        setFormData({
            name: '',
            type: 'material',
            description: '',
            quantity: 1,
            location: '',
            condition: 'good',
            requiresApproval: false
        });
    };

    const filteredResources = resources.filter(r =>
        r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 max-w-7xl animate-fade-in">
            {/* Header */}
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary-container text-primary-onContainer flex items-center justify-center shadow-sm">
                        <Inventory style={{ fontSize: 28 }} />
                    </div>
                    <div>
                        <h1 className="text-display-small font-bold text-surface-on">Ressourcen</h1>
                        <p className="text-body-medium text-surface-onVariant">{resources.length} Einträge</p>
                    </div>
                </div>

                <button
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="md3-btn-filled flex items-center gap-2"
                >
                    <Add style={{ fontSize: 20 }} />
                    Neue Ressource
                </button>
            </div>

            {/* Alert */}
            {message.text && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 animate-fade-in ${message.type === 'error' ? 'bg-error-container text-error-on-container' : 'bg-primary-container text-primary-onContainer'
                    }`}>
                    <span>{message.text}</span>
                    <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto">
                        <X style={{ fontSize: 18 }} />
                    </button>
                </div>
            )}

            {/* Filters */}
            <div className="md3-card-elevated p-4 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search style={{ fontSize: 18 }} className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-onVariant" />
                        <input
                            type="text"
                            placeholder="Suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="md3-textfield-outlined pl-12"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        <button
                            onClick={() => setFilterType('all')}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${filterType === 'all' ? 'bg-primary text-white' : 'bg-surface-variant text-surface-onVariant'
                                }`}
                        >
                            Alle
                        </button>
                        {typeOptions.map(t => (
                            <button
                                key={t.value}
                                onClick={() => setFilterType(t.value)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${filterType === t.value ? 'bg-primary text-white' : 'bg-surface-variant text-surface-onVariant'
                                    }`}
                            >
                                {t.icon} {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Resources Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map(resource => (
                    <div key={resource._id} className="md3-card-elevated p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-primary-container text-primary-onContainer flex items-center justify-center shrink-0">
                                    <Inventory style={{ fontSize: 22 }} />
                                </div>
                                <div>
                                    <h3 className="text-title-large font-bold text-surface-on">{resource.name}</h3>
                                    <span className="text-label-small text-surface-onVariant">
                                        {typeOptions.find(t => t.value === resource.type)?.label}
                                    </span>
                                </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-label-small font-medium ${resource.availability?.isAvailable
                                    ? 'bg-primary-container text-primary-onContainer'
                                    : 'bg-error-container text-error-on-container'
                                }`}>
                                {resource.availability?.isAvailable ? 'Verfügbar' : 'Belegt'}
                            </span>
                        </div>

                        {resource.description && (
                            <p className="text-body-medium text-surface-onVariant mb-4 line-clamp-2">
                                {resource.description}
                            </p>
                        )}

                        <div className="space-y-2 mb-4">
                            {resource.location && (
                                <div className="flex items-center gap-2 text-body-small text-surface-onVariant">
                                    <Location style={{ fontSize: 16 }} />
                                    <span>{resource.location}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-body-small">
                                <span className={conditionOptions.find(c => c.value === resource.condition)?.color}>
                                    Zustand: {conditionOptions.find(c => c.value === resource.condition)?.label}
                                </span>
                            </div>
                            {resource.quantity > 1 && (
                                <div className="text-body-small text-surface-onVariant">
                                    Menge: {resource.quantity}
                                </div>
                            )}
                        </div>

                        {/* Upcoming Bookings */}
                        {resource.bookings?.filter(b => b.status !== 'cancelled' && new Date(b.endDate) > new Date()).length > 0 && (
                            <div className="p-3 bg-surface-containerLow rounded-xl mb-4">
                                <p className="text-label-small text-surface-onVariant mb-2">Nächste Buchungen:</p>
                                {resource.bookings
                                    .filter(b => b.status !== 'cancelled' && new Date(b.endDate) > new Date())
                                    .slice(0, 2)
                                    .map((b, idx) => (
                                        <div key={idx} className="text-body-small flex items-center gap-2">
                                            <Calendar style={{ fontSize: 12 }} />
                                            {new Date(b.startDate).toLocaleDateString('de-DE')} - {new Date(b.endDate).toLocaleDateString('de-DE')}
                                        </div>
                                    ))}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-4 border-t border-outlineVariant">
                            <button
                                onClick={() => openBookingModal(resource)}
                                className="md3-btn-tonal flex-1"
                                disabled={!resource.availability?.isAvailable}
                            >
                                <Calendar style={{ fontSize: 18 }} className="mr-1" />
                                Buchen
                            </button>
                            <button
                                onClick={() => openEditModal(resource)}
                                className="md3-icon-btn text-primary hover:bg-primary-container"
                            >
                                <Edit style={{ fontSize: 20 }} />
                            </button>
                            <button
                                onClick={() => deleteResource(resource._id)}
                                className="md3-icon-btn text-error hover:bg-error-container"
                            >
                                <Delete style={{ fontSize: 20 }} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {filteredResources.length === 0 && (
                <div className="text-center py-16 text-surface-onVariant">
                    <Inventory style={{ fontSize: 64 }} className="mx-auto mb-4 opacity-30" />
                    <p className="text-title-medium">Keine Ressourcen gefunden</p>
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-lg w-full p-8 animate-scale-in max-h-[90vh] overflow-y-auto">
                        <h2 className="text-title-large font-bold mb-6">
                            {selectedResource ? 'Ressource bearbeiten' : 'Neue Ressource'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="md3-textfield-outlined"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Typ</label>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                        className="md3-textfield-outlined"
                                    >
                                        {typeOptions.map(t => (
                                            <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Menge</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                                        className="md3-textfield-outlined"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Beschreibung</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="md3-textfield-outlined min-h-[80px]"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Standort</label>
                                    <input
                                        type="text"
                                        value={formData.location}
                                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                        className="md3-textfield-outlined"
                                        placeholder="z.B. Materialraum"
                                    />
                                </div>
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Zustand</label>
                                    <select
                                        value={formData.condition}
                                        onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                                        className="md3-textfield-outlined"
                                    >
                                        {conditionOptions.map(c => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <label className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface-containerLow cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.requiresApproval}
                                    onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                                    className="accent-primary w-5 h-5"
                                />
                                <span className="text-body-medium">Buchung erfordert Genehmigung</span>
                            </label>

                            <div className="flex gap-3 pt-4 border-t border-outlineVariant">
                                <button type="submit" className="md3-btn-filled flex-1">
                                    {selectedResource ? 'Speichern' : 'Erstellen'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="md3-btn-text flex-1"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Booking Modal */}
            {showBookingModal && selectedResource && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-md w-full p-8 animate-scale-in">
                        <h2 className="text-title-large font-bold mb-2">Ressource buchen</h2>
                        <p className="text-body-medium text-surface-onVariant mb-6">{selectedResource.name}</p>

                        <form onSubmit={handleBooking} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Von *</label>
                                    <input
                                        type="date"
                                        value={bookingData.startDate}
                                        onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value })}
                                        className="md3-textfield-outlined"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Bis *</label>
                                    <input
                                        type="date"
                                        value={bookingData.endDate}
                                        onChange={(e) => setBookingData({ ...bookingData, endDate: e.target.value })}
                                        className="md3-textfield-outlined"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Notiz</label>
                                <textarea
                                    value={bookingData.note}
                                    onChange={(e) => setBookingData({ ...bookingData, note: e.target.value })}
                                    className="md3-textfield-outlined"
                                    placeholder="Wofür wird die Ressource benötigt?"
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button type="submit" className="md3-btn-filled flex-1">
                                    Buchen
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowBookingModal(false); setBookingData({ startDate: '', endDate: '', note: '' }); }}
                                    className="md3-btn-text flex-1"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Resources;
